import BootstrapDialog from '@/components/Dialog/BootstrapDialog';
import { MaskedIcon } from '@/components/MaskedIcon/MaskedIcon';
import { toast } from '@/components/toast';
import { useGetSupportUrl } from '@/hooks/useGetSupportUrl';
import LoadingAnimation from '@/images/LoadingAnimation.json';
import Lock from '@/images/Lock.svg';
import { useAnalytics } from '@/services/analytics';
import { Billing } from '@/services/api';
import { isStripePayingUser, useAuth } from '@/services/auth';
import { useFlag } from '@/services/featureFlags';
import { logger } from '@/services/logger';
import { isDowngrading, useGetDataForChangePlan } from '@/state/products/Products';
import theme from '@/theme';
import { toCamelCase } from '@/utils/utils';
import { ICouponData, PlanGroup } from '@d-id/types';
import styled from '@emotion/styled';
import { Player } from '@lottiefiles/react-lottie-player';
import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, DialogContent, DialogTitle, IconButton, Typography } from '@mui/material';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Stripe from 'stripe';
import Button from '../../Buttons/Button';
import { DowngradeModal } from '../DowngradeModal';
import { DetailsLines, TotalLine } from './Lines';
import { Promotion } from './Promotion';
import { useUpdateSubscription } from './useUpdateSubscription';
import { productName } from './utils';

const Title = styled.div`
    margin-bottom: 0.5rem;
    margin-top: 0.8rem;
    text-align: center;
    font-style: normal;
    font-weight: 700;
    font-size: 24px;
    line-height: 30px;
    letter-spacing: -0.02em;
`;

const Lines = styled.div`
    padding-bottom: 24px;
    border-bottom: 1px solid #f1f2f6;
    width: 100%;
`;

const ButtonText = styled.div`
    margin-right: 1rem;
    font-style: normal;
    font-weight: 700;
    font-size: 14px;
    line-height: 18px;
`;

const StyledDialogTitle = styled(DialogTitle)`
    font-size: 1.6rem;
    justify-content: center;
    flex-direction: column;
    margin-bottom: 10px;
`;

const StyledDialogContent = styled(DialogContent, { shouldForwardProp: p => p !== 'isSpinner' })<{ isSpinner: boolean }>(
    ({ isSpinner }) => ({
        minHeight: isSpinner ? '25rem' : '10rem',
        padding: '0 40px !important',
        transition: 'min-height 0.75s ease-in',
    })
);

const StyledIconButton = styled(IconButton)`
    position: absolute;
    right: 1rem;
    color: ${theme.colors.primary};
    top: 0.5rem;
`;

const Loading = styled(Player)`
    height: 25rem;
    width: 70%;
`;

const StyledButton = styled(Button)({
    padding: '0.5rem 40px',
    width: '100%',
    height: '44px',
});

const StyledDialogActions = styled(DialogActions)`
    margin-top: 1.75rem;
    margin-bottom: 2.5rem;
    padding: 0 40px !important;
`;

const CreditCardTextWrapper = styled.div<{ show: boolean }>`
    display: ${props => (props.show ? 'flex' : 'none')};
    align-items: center;
    align-self: start;
    justify-content: space-between;
    width: 100%;
`;

const CreditCardText = styled.div`
    color: #7c7c7a;
    font-style: normal;
    font-weight: 400;
    font-size: 12px;
    line-height: 16px;
    display: flex;
    flex-direction: row;
    align-items: center;
`;

const ProductLabel = styled(Typography)`
    background-color: ${({ theme }) => theme.colors.primary.bg};
    color: ${({ theme }) => theme.colors.primary.main};
    width: fit-content;
    padding: 0.25rem 1rem;
    border-radius: 4px;
    letter-spacing: normal !important;
`;

const StyledMessage = styled.div`
    font-size: 1.6rem;
    margin-left: 5px;
    width: 500px;
`;

const SupportLink = styled(Link)`
    font-weight: 600;
`;

const NO_PAYMENT_DATA = 'no payment data';

interface Props {
    setIsOpen: (isOpen: boolean) => void;
    newPlan: PlanGroup;
    newInterval: Billing;
    openPostCheckout: () => void;
    newPriceId: string;
    couponToApply?: ICouponData['coupon'];
    onClose?: () => void;
}

export interface ChangePlanData {
    payments: {
        proration: number;
        nonProration: number;
    };
    creditCardDetails?: Stripe.PaymentMethod.Card;
    nextBillingDate: Date;
    mixpanelData: Record<string, any>;
    newPriceCredits: string;
    prevPriceCredits?: string;
}

export const ChangePlanModal = ({ setIsOpen, newPriceId, newPlan, newInterval, openPostCheckout, couponToApply, onClose }: Props) => {
    const { user } = useAuth();
    const [coupon, setCoupon] = useState<ICouponData['coupon'] | null>(couponToApply);
    const [promoCode, setPromoCode] = useState<string | null>(null);
    const mixpanel = useAnalytics();
    const supportUrl = useGetSupportUrl();

    const {
        data: changePlanData,
        error,
        isPreviousData,
    } = isStripePayingUser(user)
        ? useGetDataForChangePlan({
              priceId: newPriceId,
              coupon: coupon?.id,
              productName: newPlan,
              interval: newInterval,
              onError: () => setIsOpen(false),
          })
        : { data: undefined, error: NO_PAYMENT_DATA, isPreviousData: false };
    const mixpanelData = { ...(changePlanData?.mixpanelData ?? {}), promoCode };
    const { updateSubscription, isUpdating } = useUpdateSubscription({
        mixpanelData,
        openPostCheckout,
        nextBillingDate: changePlanData?.nextBillingDate,
        newPlan,
        newPriceCredits: parseInt(changePlanData?.newPriceCredits || '0'),
    });

    const currentPrice = parseInt(user?.priceCredits ?? '0');
    // we want to show the downgrade modal only if the user is downgrading to a lower plan, not if for example downgrade from yearly to monthly
    const isDowngradingPlan = isDowngrading(user?.planGroup || PlanGroup.TRIAL, currentPrice, newPlan as PlanGroup, currentPrice);
    const showDownGradeModalFF = useFlag('show_downgrade_plan_modal', false);
    const [showDowngradeModal, setShowDowngradeModal] = useState(showDownGradeModalFF && isDowngradingPlan);

    const handleConfirm = async () => {
        mixpanel?.track('change-plan', {
            event: 'click',
            ...mixpanelData,
        });

        await updateSubscription({ priceId: newPriceId, coupon: coupon?.id });
        setIsOpen(false);
    };

    useEffect(() => {
        if (!changePlanData) {
            window.history.replaceState({}, document.title);
        } else {
            mixpanel?.track('change-plan', {
                event: 'view',
                ...mixpanelData,
            });
        }
    }, [changePlanData?.newPriceId]);

    useEffect(() => {
        if (!!error) {
            logger.error(error);
            if (error === NO_PAYMENT_DATA) {
                toast.error({
                    render: (
                        <StyledMessage>
                            Please contact
                            <SupportLink to={supportUrl} target="_blank">
                                {' support@d-id.com '}
                            </SupportLink>
                            for plan changes
                        </StyledMessage>
                    ),
                    title: 'Please contact support@d-id.com for plan changes',
                });
            } else {
                toast.error({ title: 'Something went wrong. Please try again' });
            }
            setIsOpen(false);
        }
    }, [error]);

    const handleClose = () => {
        setShowDowngradeModal(false);
        setIsOpen(false);
        onClose?.();
    };

    const renderContent = useCallback(() => {
        if (!changePlanData) {
            return <Loading src={LoadingAnimation} autoplay loop />;
        }

        return (
            <>
                <Lines>
                    <ProductLabel variant="h6">{`${productName(newPlan)?.toUpperCase()} PLAN (${
                        changePlanData!.newPriceCredits
                    })`}</ProductLabel>
                    <DetailsLines
                        changePlanData={changePlanData}
                        newInterval={newInterval}
                        discounted={!!coupon && !isPreviousData}
                        user={user!}
                    />
                </Lines>
                {changePlanData!.payments.proration > 0 && (
                    <TotalLine changePlanData={changePlanData} discounted={!!coupon && !isPreviousData} />
                )}
                <CreditCardTextWrapper show={!!changePlanData!.creditCardDetails}>
                    <CreditCardText>
                        {toCamelCase(changePlanData?.creditCardDetails?.brand) ?? 'Card' + '  '}
                        <h3>&nbsp;&#x2022;&#x2022;&#x2022;&#x2022;</h3>
                        {changePlanData?.creditCardDetails?.last4}
                    </CreditCardText>
                    <CreditCardText>
                        <MaskedIcon src={Lock} color="#7C7C7A" size="1.6rem" margin="0 5px 0 0" />
                        Card information is stored securely
                    </CreditCardText>
                </CreditCardTextWrapper>
                <Promotion
                    coupon={coupon}
                    isOldCoupon={isPreviousData}
                    setCoupon={setCoupon}
                    priceId={newPriceId}
                    plan={newPlan}
                    promoCode={promoCode}
                    setPromoCode={setPromoCode}
                />
            </>
        );
    }, [changePlanData, newPlan, newInterval, coupon, user]);

    if (showDowngradeModal) {
        return (
            <DowngradeModal
                newPlan={newPlan}
                oldPlan={user?.planGroup || PlanGroup.TRIAL}
                onConfirm={() => setShowDowngradeModal(false)}
                onClose={handleClose}
            />
        );
    }

    return (
        <BootstrapDialog
            onClose={handleClose}
            open
            width="450px"
            contentstyle={{ flex: 1, overflow: 'auto', alignItems: 'center' }}
            PaperProps={{ style: { borderRadius: '12px' } }}>
            <StyledDialogTitle id="delete-image-modal">
                <Title>{changePlanData ? 'Confirm' : 'Fetching'} your new plan</Title>
                <StyledIconButton aria-label="close" onClick={handleClose}>
                    <CloseIcon />
                </StyledIconButton>
            </StyledDialogTitle>
            <StyledDialogContent isSpinner={!changePlanData}>{renderContent()}</StyledDialogContent>
            <StyledDialogActions>
                <StyledButton onClick={handleConfirm} aria-label="delete" disabled={isUpdating || !changePlanData}>
                    <ButtonText>Confirm payment</ButtonText>
                </StyledButton>
            </StyledDialogActions>
        </BootstrapDialog>
    );
};
